import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Activity,
  Eye,
  Zap,
  Brain,
  Shield,
  Lock,
  EyeOff,
  FileText,
} from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

const wait = (ms) => new Promise((res) => setTimeout(res, ms))

// ─── Hero Visualization ───────────────────────────────────────────────────────

function HeroVisualization() {
  const [phase, setPhase] = useState(0)

  // 0: call in progress
  // 1: prospect question pops over Sarah's tile
  // 2: focus shifts to the rep — silence (animated dots)
  // 3: focus shifts to Rithmo — answer pops over its tile
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setPhase(0)
        await wait(1700)
        if (cancelled) break
        setPhase(1)
        await wait(3100)
        if (cancelled) break
        setPhase(2)
        await wait(3500)
        if (cancelled) break
        setPhase(3)
        await wait(5000)
      }
    }
    run()
    return () => { cancelled = true }
  }, [])

  const focusByPhase = { 1: 'sarah', 2: 'rep', 3: 'rithmo' }
  const focus = focusByPhase[phase] ?? null
  const tileOpacity = (id) => (focus === null || focus === id ? 1 : 0.45)

  return (
    <div className="relative w-full max-w-[38.7rem] mx-auto lg:mx-0">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="rounded-2xl bg-dark-800/90 border border-dark-400/30 p-4 shadow-premium"
      >
        {/* Meeting header — wraps the whole tile group */}
        <div className="flex items-center justify-between mb-4 px-1">
          <div className="flex items-center gap-2">
            <motion.div
              className="w-2 h-2 rounded-full bg-error"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            <span className="text-xs text-gray-400 font-mono">Live · Acme Corp · Q4 Proposal Review</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-accent-400" />
            <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider">Rithmo Active</span>
          </div>
        </div>

        {/* Participant tiles */}
        <div className="grid grid-cols-3 gap-2.5">
          {/* Sarah Chen — prospect */}
          <motion.div
            animate={{ opacity: tileOpacity('sarah') }}
            transition={{ duration: 0.4 }}
            className={`relative aspect-[3/4] rounded-xl bg-dark-600 border transition-colors duration-300 ${
              focus === 'sarah' ? 'border-success ring-1 ring-success/50' : 'border-dark-400/40'
            }`}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-dark-400 flex items-center justify-center">
                <span className="text-sm font-semibold text-white">SC</span>
              </div>
            </div>
            <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-dark-900/80">
              <span className="text-[10px] text-gray-200 whitespace-nowrap">Sarah Chen <span className="text-gray-400">· Prospect</span></span>
            </div>

            {/* Prospect question bubble */}
            <AnimatePresence>
              {phase >= 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.4 }}
                  className="absolute top-2 left-2 z-20 w-56 bg-white border border-gray-200 rounded-xl rounded-bl-sm px-3 py-2 shadow-card-hover"
                >
                  <p className="text-xs text-gray-800 leading-snug">
                    "Before we go further, are you SOC 2 compliant?"
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Rep */}
          <motion.div
            animate={{ opacity: tileOpacity('rep') }}
            transition={{ duration: 0.4 }}
            className={`relative aspect-[3/4] rounded-xl bg-dark-600 border transition-colors duration-300 ${
              focus === 'rep' ? 'border-dark-200/60 ring-1 ring-dark-200/30' : 'border-dark-400/40'
            }`}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-dark-400 flex items-center justify-center">
                <span className="text-sm font-semibold text-white">SR</span>
              </div>
            </div>
            <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-dark-900/80">
              <span className="text-[10px] text-gray-200 whitespace-nowrap">Sales Rep</span>
            </div>

            {/* The fumble — silence dots */}
            <AnimatePresence>
              {phase === 2 && (
                <motion.div
                  initial={{ opacity: 0, y: 8, x: '-50%', scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
                  exit={{ opacity: 0, y: 4, x: '-50%' }}
                  transition={{ duration: 0.35 }}
                  className="absolute top-2 left-1/2 z-20 bg-white border border-gray-200 rounded-xl rounded-bl-sm px-3 py-2.5 shadow-card-hover flex items-center gap-1.5"
                >
                  {[0, 1, 2].map(i => (
                    <motion.div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-gray-400"
                      animate={{ opacity: [0.25, 1, 0.25] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Rithmo — AI, visually distinct */}
          <motion.div
            animate={{ opacity: tileOpacity('rithmo') }}
            transition={{ duration: 0.4 }}
            className={`relative aspect-[3/4] rounded-xl bg-gradient-to-b from-dark-600 to-dark-700 border transition-colors duration-300 ${
              focus === 'rithmo' ? 'border-accent-400 ring-1 ring-accent-400/50 shadow-glow-sm' : 'border-accent-500/30'
            }`}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <motion.div
                  className="absolute -inset-1.5 rounded-full border border-accent-400/50"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
                  transition={{ duration: 2.2, repeat: Infinity }}
                />
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-glow-sm p-2">
                  <img src="/rithmo-mark.png" alt="Rithmo" className="w-full h-full object-contain" />
                </div>
              </div>
            </div>
            <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-dark-900/80">
              <span className="text-[10px] font-medium text-accent-400 whitespace-nowrap">Rithmo <span className="text-dark-300">· AI</span></span>
            </div>

            {/* Rithmo answer bubble */}
            <AnimatePresence>
              {phase >= 3 && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.4 }}
                  className="absolute top-2 right-2 z-20 w-60 bg-dark-700 border border-accent-500/40 rounded-xl rounded-br-sm px-3 py-2 shadow-glow-sm"
                >
                  <div className="flex items-center gap-1 mb-1">
                    <img src="/rithmo-mark.png" alt="" className="w-3 h-3 object-contain" />
                    <span className="text-[9px] font-semibold text-accent-400 uppercase tracking-wider">Rithmo</span>
                  </div>
                  <p className="text-xs text-white leading-snug">
                    Yes, we are SOC 2 compliant. Here is a link to the documentation.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.4 }}
        className="absolute -top-4 -right-4 badge-accent text-sm px-4 py-2 shadow-glow-sm animate-pulse-slow"
      >
        In the Room
      </motion.div>
    </div>
  )
}

// ─── Deal Intelligence Visualization ─────────────────────────────────────────

function DealIntelligenceViz() {
  const steps = [
    { label: 'Every open question tracked until it is answered', dot: 'bg-warning' },
    { label: 'Every commitment flagged before it slips', dot: 'bg-orange-400' },
    { label: 'Every objection remembered with full context', dot: 'bg-error' },
    { label: 'Every buying signal carried into the next call', dot: 'bg-primary-300' },
  ]

  return (
    <div className="relative max-w-md mx-auto">
      <div className="flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="relative w-24 h-24 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow"
        >
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center p-2.5">
            <img src="/rithmo-mark.png" alt="Rithmo" className="w-full h-full object-contain" />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-primary opacity-20 animate-pulse-slow" />
        </motion.div>
      </div>
      <div className="flex justify-center"><div className="w-0.5 h-6 rounded-full bg-white/20" /></div>

      {steps.map((step, i) => (
        <div key={step.label}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15 }}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 border border-white/10 backdrop-blur-sm"
          >
            <motion.div
              className={`w-2 h-2 rounded-full flex-shrink-0 ${step.dot}`}
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
            />
            <span className="text-sm font-medium text-gray-200">{step.label}</span>
          </motion.div>
          <div className="flex justify-center"><div className="w-0.5 h-6 rounded-full bg-white/20" /></div>
        </div>
      ))}

      {/* The payoff */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.7 }}
        className="flex items-center justify-center gap-2.5 px-4 py-4 rounded-xl bg-accent-500/15 border border-accent-400/40 shadow-glow-sm"
      >
        <TrendingUp className="w-5 h-5 text-accent-400 flex-shrink-0" />
        <span className="text-base font-semibold text-accent-300">Nothing slips. The deal advances.</span>
      </motion.div>

      {/* The teaser */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1 }}
        className="text-center text-dark-200 text-sm mt-5"
      >
        And deals where nothing slips have a way of closing.
      </motion.p>
    </div>
  )
}

// ─── FAQ Accordion ────────────────────────────────────────────────────────────

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="font-semibold text-gray-900">{question}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 ml-4"
        >
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-gray-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Page Data ────────────────────────────────────────────────────────────────

const painPoints = [
  {
    icon: <Users className="w-6 h-6" />,
    title: 'Three of Them. One of Your Rep.',
    problem: 'The champion, the tech lead, the lawyer: each with their own questions, each waiting on an answer. Your rep is defending every front while trying to keep the deal moving forward.',
    solution: 'Rithmo is in the call as backup, tracking every question and every thread, so nothing gets dropped while your rep runs the conversation.',
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: 'Their CTO Made the Call. Yours Couldn\'t.',
    problem: 'The technical questions are coming either way. Without an expert on your side of the call, every deep question becomes a "let me get back to you."',
    solution: 'Rithmo surfaces the answer, the documentation, and the spec live, the moment the question lands.',
  },
  {
    icon: <Brain className="w-6 h-6" />,
    title: 'Your Prospect Keeps Score.',
    problem: 'They remember the concern that never got fully answered and the follow-up that never came. Your rep is answering from memory, under pressure.',
    solution: 'Rithmo carries the full history of the deal into the call and flags every open thread before the prospect has to.',
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: 'New Reps Walk In With Nothing.',
    problem: 'No history with the account. No read on what this buyer cares about. The ramp period exists to compensate for starting from zero, and it is expensive.',
    solution: 'Every rep walks in carrying everything your team has learned on the deal, from their very first call.',
  },
]

const features = [
  {
    title: 'In the Room, Not Watching From Outside It',
    description: 'Most sales intelligence tools record the meeting and deliver a summary after it ends. Rithmo is a participant, tracking what is being said, what is going unanswered, and what the prospect just revealed that connects to something they said in an earlier conversation. The analysis does not wait until the call ends.',
    icon: <Eye className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Active During Call · Acme Corp</p>
        <div className="flex items-center gap-2 p-3 rounded-xl bg-accent-50 border border-accent-200">
          <motion.div
            className="w-2 h-2 rounded-full bg-accent-500 flex-shrink-0"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <span className="text-xs font-medium text-accent-700">Rithmo · Active Participant</span>
        </div>
        {[
          { label: 'Tracking', value: 'Open threads from prior meetings' },
          { label: 'Flagged', value: '2 unresolved commitments' },
          { label: 'Surfaced', value: 'Budget signal: Q4 pressure' },
        ].map(r => (
          <div key={r.label} className="flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500">{r.label}</span>
            <span className="text-xs font-semibold text-gray-700 text-right">{r.value}</span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-accent text-xs">Active · Not visible to prospect</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Deal Memory That Never Degrades',
    description: 'Every meeting adds to Rithmo\'s picture of this deal: what this prospect cares about, what concerns have been raised, what commitments have been made, what is still unresolved. That picture sharpens with every conversation and carries into every meeting that follows.',
    icon: <Brain className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Deal Context · Carried Forward</p>
        {[
          { label: 'Stated concerns', value: '4 tracked', ok: true },
          { label: 'Open threads', value: '2 unresolved', ok: false },
          { label: 'Commitments made', value: '3 logged', ok: true },
          { label: 'Key signals', value: 'Budget, timeline, stakeholders', ok: true },
        ].map(r => (
          <div key={r.label} className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-600">{r.label}</span>
            <span className={`text-xs font-semibold ${r.ok ? 'text-accent-600' : 'text-warning'}`}>{r.value}</span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-accent text-xs">Every meeting sharpens the picture</span>
        </div>
      </div>
    ),
  },
  {
    title: 'What Worked. Not Just What Happened.',
    description: 'Rithmo tracks what gets surfaced, whether it gets said, and how the deal moves afterward. That loop closes after every meeting. The result is not a static playbook. It is a live read of what is actually working in this specific deal, with this specific prospect, built from everything that has been tried.',
    icon: <Activity className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Closed-Loop · Deal Signals</p>
        {[
          { approach: 'Addressed security concern directly', outcome: 'Deal advanced', ok: true },
          { approach: 'Raised pricing before discovery complete', outcome: 'Engagement dropped', ok: false },
          { approach: 'Referenced prior commitment by name', outcome: 'Trust signal', ok: true },
        ].map(r => (
          <div key={r.approach} className="space-y-0.5 pb-2 border-b border-gray-100 last:border-0 last:pb-0">
            <p className="text-xs text-gray-600">{r.approach}</p>
            <p className={`text-xs font-semibold ${r.ok ? 'text-success' : 'text-error'}`}>{r.outcome}</p>
          </div>
        ))}
        <div className="pt-1">
          <span className="badge-accent text-xs">Updated after every meeting</span>
        </div>
      </div>
    ),
  },
  {
    title: 'The Rep Leads. Rithmo Has Their Back.',
    description: 'Rithmo starts by surfacing what the rep needs: the right context at the right moment, the open thread that needs closing, the signal from a prior conversation that matters right now. All of it invisible to the prospect. What they notice is a rep who remembers everything and follows through on every commitment. The rep drives. Rithmo makes sure nothing is missed.',
    icon: <Users className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">How Rithmo Operates</p>
        {[
          { mode: 'Surfaces context privately', desc: 'Rep sees what\'s relevant. Prospect sees a rep who remembers everything.', coming: false },
          { mode: 'Tracks open threads', desc: 'Every unanswered question, every deferred commitment, every signal.', coming: false },
          { mode: 'Steps in directly', desc: 'Closes threads. Answers questions. Picks up what might be missed.', coming: true },
        ].map(r => (
          <div
            key={r.mode}
            className={`p-3 rounded-xl border ${r.coming ? 'border-dashed border-gray-200 opacity-60' : 'border-accent-100 bg-accent-50/50'}`}
          >
            <p className="text-xs font-semibold text-gray-800 mb-0.5">
              {r.mode}{r.coming && <span className="text-gray-400 font-normal"> · Coming</span>}
            </p>
            <p className="text-xs text-gray-500">{r.desc}</p>
          </div>
        ))}
      </div>
    ),
  },
]

const faqs = [
  {
    question: 'I already use a conversation intelligence tool. How is Rithmo different?',
    answer: 'Most conversation intelligence tools analyze meetings after they happen: transcripts, summaries, and behavioral flags delivered once the call is over. Rithmo is present during the call, surfacing deal context, open threads from prior meetings, and prospect signals at the moment they are relevant. The intelligence does not arrive after the decision has already been made.',
  },
  {
    question: 'Does Rithmo replace the sales rep?',
    answer: 'Today, Rithmo supports reps, surfacing what they need, filling in what they might miss, and carrying the full deal history into every conversation. Over time, as Rithmo\'s understanding of a deal deepens, it can take a more active role. The rep leads, and that changes as trust is built, in both directions.',
  },
  {
    question: 'How does Rithmo build context on a deal?',
    answer: 'Rithmo attends every meeting in the deal cycle. Every conversation adds to its understanding: what the prospect has said, what has been committed to, what is still unresolved, and what signals have emerged. That context carries into every meeting that follows, from the first to the last.',
  },
  {
    question: 'Does Rithmo surface anything to the prospect, or only to the rep?',
    answer: 'Everything Rithmo surfaces is private to the rep. Prospects experience the conversation normally. What they notice is a rep who remembers everything and never lets a commitment fall through.',
  },
  {
    question: 'Does Rithmo work with our existing meeting platform?',
    answer: 'Rithmo works alongside Zoom, Google Meet, and Microsoft Teams. No workflow disruption, no migration, no new platform to adopt.',
  },
  {
    question: 'What does Rithmo track between meetings?',
    answer: 'Every commitment made, every question raised, every concern surfaced, every signal the prospect gives about what matters to them. Rithmo holds a complete picture of the deal and brings it into the room for every conversation that follows.',
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

function SalesPage() {
  usePageMeta({
    title: 'Rithmo for Sales: Deal Intelligence That Lives in the Room',
    description: 'Rithmo joins every sales meeting as an active participant, carrying the full history of every conversation, surfacing what matters in the moment, and getting sharper on every deal with every meeting it attends.',
  })

  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/50 to-white" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-4">
                <span className="badge-primary text-sm">For Sales Teams</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                The Meeting is Live.{' '}
                <span className="gradient-text">Your Sales AI isn't.</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
                Rithmo joins every sales conversation as an active participant, carrying the full history of every interaction with your prospect, surfacing what matters in the moment, and getting sharper on your deal with every meeting it attends.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4 text-center">
                  Get Early Access
                </Link>
              </div>
            </motion.div>

            <div className="flex justify-center lg:justify-end">
              <HeroVisualization />
            </div>
          </div>
        </div>
      </section>

      {/* ── Pain Points ───────────────────────────────────────────────────── */}
      <section className="pt-12 pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Their Whole Team Is on the Call.{' '}
              <span className="gradient-text">Your Rep Is in There Alone.</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              The champion brings their tech lead. The tech lead brings the lawyer. Your rep is fielding all of it solo, keeping the call moving while everything your team knows about this deal sits somewhere they can't reach.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {painPoints.map((point, i) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-interactive group flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-glow-sm group-hover:shadow-glow transition-shadow duration-300 text-white">
                  {point.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{point.problem}</p>
                <div className="mt-auto pt-4">
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-accent-500 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-accent-600 uppercase tracking-wider">With Rithmo</span>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{point.solution}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── From Open Thread to Closed Deal ───────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                The Right Context.{' '}
                <span className="gradient-text">At the Exact Moment It Matters.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Rithmo doesn't wait until the call is over to tell you what happened. It tracks every open thread from every prior conversation and surfaces what's relevant while the deal is still live: the unanswered question, the missed commitment, the signal the prospect gave that nobody followed up on.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="space-y-3">

              {/* Beat 1: Captured weeks ago */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5"
              >
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">One Week Ago · Discovery Call</p>
                <p className="text-sm text-gray-600 italic mb-3 border-l-2 border-gray-200 pl-3">
                  "We'd need to see documentation on data residency before we could move forward."
                </p>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-warning flex-shrink-0" />
                  <p className="text-xs text-gray-400">Rithmo flags a blocking thread. Unresolved.</p>
                </div>
              </motion.div>

              <div className="flex justify-center"><div className="w-0.5 h-7 rounded-full bg-gray-300" /></div>

              {/* Beat 2: Surfaced live, privately */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-dark-700 rounded-2xl p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <motion.div
                      className="w-2 h-2 rounded-full bg-error flex-shrink-0"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1.4, repeat: Infinity }}
                    />
                    <span className="text-xs font-semibold text-white uppercase tracking-widest">Today · Next Steps</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
                    <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider">Rithmo</span>
                  </div>
                </div>
                <p className="text-sm text-white leading-snug mb-3">
                  Still open from the discovery call: Sarah is waiting on data residency documentation. Raise it before she has to ask again.
                </p>
                <div className="flex items-center gap-1.5">
                  <EyeOff className="w-3 h-3 text-dark-300 flex-shrink-0" />
                  <span className="text-[10px] text-dark-300">Only the rep sees this</span>
                </div>
              </motion.div>

              <div className="flex justify-center"><div className="w-0.5 h-7 rounded-full bg-gray-300" /></div>

              {/* Beat 3: Thread closed */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-success/5 border border-success/20"
              >
                <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                <span className="text-sm text-success font-medium">The rep raises it first. Sarah never has to ask twice.</span>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-primary-50/30 to-white" />
        <div className="absolute -top-20 right-10 w-72 h-72 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-10 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              How Rithmo{' '}
              <span className="gradient-text">Evens the Call.</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Four ways Rithmo turns everything your team knows into backup your rep can actually use, live.
            </p>
          </motion.div>

          <div className="space-y-20">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-glow-sm text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
                <div className={i % 2 === 1 ? 'lg:col-start-1' : ''}>{feature.visual}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Deal Intelligence (dark section) ──────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Rithmo Advances Deals.{' '}
              <span className="gradient-text">Period.</span>
            </h2>
            <p className="text-lg text-dark-100 max-w-2xl mx-auto leading-relaxed">
              Not a note taker. Not a list of generic tips flashed at a critical moment. Rithmo is an active sales support agent: fully context aware, never forgetting, live in the call. Every open question, every commitment, every signal is tracked until it is closed.
            </p>
          </motion.div>

          <DealIntelligenceViz />

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="text-center text-dark-200 mt-10 text-lg"
          >
            Rithmo can't guarantee the close.
            <br />
            <span className="text-white font-medium">It guarantees nothing slips through the cracks.</span>
          </motion.p>
        </div>
      </section>

      {/* ── Works With Your Stack ─────────────────────────────────────────── */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <p className="text-lg text-gray-700 text-center md:text-left">
              <span className="font-semibold text-gray-900">No migration. No new workflow.</span>{' '}
              Rithmo joins the meetings your team already runs.
            </p>
            <div className="flex items-center gap-3">
              {['Zoom', 'Google Meet', 'Microsoft Teams'].map(platform => (
                <span key={platform} className="px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap">{platform}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-4">Security and Trust</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Built to Pass the{' '}
              <span className="gradient-text">Security Review</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Deal intelligence is only valuable when it's trusted. Rithmo is designed to satisfy security and procurement teams, not just sales leaders.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Shield className="w-6 h-6" />,
                title: 'Data Is Never Sold or Shared',
                body: 'Conversation content belongs to your organization. Rithmo does not sell, license, or share meeting data with any third party under any circumstances.',
              },
              {
                icon: <Lock className="w-6 h-6" />,
                title: 'Encrypted at Rest and in Transit',
                body: 'All data is encrypted using AES-256 at rest and TLS 1.3 in transit. Meeting content is never stored as raw recordings.',
              },
              {
                icon: <EyeOff className="w-6 h-6" />,
                title: 'No Recording Retention',
                body: 'Rithmo analyzes meetings for deal signals. Raw audio is processed in-flight and never written to permanent storage.',
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Fully Organization-Scoped',
                body: 'Data is completely isolated to your organization. Nothing is shared across customers or accessible by other teams using Rithmo.',
              },
              {
                icon: <CheckCircle className="w-6 h-6" />,
                title: 'Access Revocable Instantly',
                body: 'Rithmo connects via Zoom OAuth. Any Zoom admin can disconnect access immediately from the admin dashboard without contacting support.',
              },
              {
                icon: <FileText className="w-6 h-6" />,
                title: 'Compliance-Minded by Design',
                body: 'Rithmo is built with SOC 2 principles in mind. Security documentation and data processing agreements are available upon request.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-accent-500/30 hover:shadow-md transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-primary flex items-center justify-center text-white mb-4 shadow-glow-sm">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Future Vision ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 60% 50%, rgba(22,193,178,0.15) 0%, transparent 60%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mx-auto mb-6 shadow-glow">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              What If the Backup{' '}
              <span className="gradient-text">Could Take the Call?</span>
            </h2>
            <p className="text-lg text-dark-100 leading-relaxed">
              Today, Rithmo backs the rep: every conversation remembered, every thread tracked, the right context surfaced live. Now picture an AI with the judgment of your best closer and the memory of every deal ever run. Zero ramp time. No bad days. One that doesn't just support the person in the room, but can be the person in the room. That's the question Rithmo was built to ask.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative glass rounded-3xl p-12 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-primary opacity-5" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-accent-500/20 rounded-full blur-3xl" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Bring Rithmo Into Your Next Sales Meeting
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
                Rithmo is working with early sales teams who want to be the first to experience deal intelligence that travels with every conversation.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                  Get Early Access
                </Link>
                <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                  Talk To The Team
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card divide-y divide-gray-100"
          >
            {faqs.map(faq => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default SalesPage
